import React from 'react'
import PropTypes from 'prop-types'
import {Button, DatePicker, Input, InputNumber, Radio, Select, Tag} from 'antd'
import {DownOutlined, UpOutlined} from '@ant-design/icons'
import moment from 'moment'
import {
    DEFAULT_GROUP_LABEL,
    GROUP_NAME_MAX_LENGTH,
    GROUP_NOTE_MAX_LENGTH,
    GROUP_VERIFY_LABELS,
    RATE_UNIT_EXAMPLES,
    RATE_UNIT_LABELS,
    PRIORITY_MANUAL_MAX,
    PRIORITY_POINTS,
} from '../../constants'
import {ceilTo, dateOnlyIso, formatInt, formatRate, roundRate} from '../../helpers/format'
import {effectivePriority, isDefaultGroup, isEffectiveAt, isManualPriority, isActiveZeroRate} from '../../helpers/priority'

const {Option} = Select

/**
 * Một thẻ nhóm điều kiện của SCR-M16-002 (§3.4).
 *
 * Nhóm MẶC ĐỊNH (Mọi SKU + không khai NVL) có viền đứt và nền nhạt để phân biệt bằng mắt, luôn thua
 * mọi nhóm khác về độ ưu tiên, và mang tên CỐ ĐỊNH `DEFAULT_GROUP_LABEL` — không đặt tên riêng được
 * (service trả 400 VAL-19), không đặt độ ưu tiên được (VAL-17).
 *
 * ⚠️ Badge số thứ tự của nhóm đó vẫn hiện chữ "A". Nó chỉ còn là DẤU NHẬN DẠNG thị giác, không còn
 *    là một phần của tên như hồi nhãn là "Nhóm A (ALL)".
 *
 * Header phải đọc được KHI THU GỌN: tên · phạm vi · hiệu lực · verify · độ ưu tiên · chỉ số. Thu gọn
 * là trạng thái mặc định khi công đoạn có nhiều nhóm, nên thiếu một trong số đó là người dùng phải mở
 * từng thẻ ra mới biết thẻ nào là thẻ nào.
 */
function GroupCard({
    group,
    index,
    materials,
    skus,
    skuSearching,
    skuSearchFailed,
    onSkuSearch,
    rateUnit,
    byDetect,
    collapsed,
    readOnly,
    onToggle,
    onChange,
    onDuplicate,
    onRemove,
    onPasteSku,
}) {
    const materialCodes = group.materialCodes || []
    const isAllGroup = isDefaultGroup(group)
    // Ô TRỐNG cũng cho `Number(null) === 0`, nên phải loại nó ra: "chưa nhập" (V-04 lo) khác hẳn
    // "khai 0 giây". Trộn hai thứ là dán nhãn "không qua công đoạn" lên một ô người dùng chưa điền.
    // PHẢI dùng cùng vị từ với `validateGroups`, xem docblock `isActiveZeroRate`.
    const zeroRate = isActiveZeroRate(group)
    const materialName = (code) => {
        const found = materials.find((item) => item.materialCode === code)

        return found ? found.name : code
    }

    const unitText = RATE_UNIT_LABELS[rateUnit] || ''
    const example = RATE_UNIT_EXAMPLES[rateUnit]

    const manualPriority = isManualPriority(group)

    const update = (patch) => {
        const next = {...group, ...patch}

        // Bất biến: nhóm ALL luôn 0 điểm (BR-M16-44). Đổi điều kiện thành ALL thì bỏ số đặt tay ngay
        // tại đây, khỏi chờ người dùng phát hiện ô đã khoá mà số vẫn còn.
        //
        // Trả về CHẤM TỰ ĐỘNG (`null` + source `computed`), không phải "đặt tay 0": gửi số cho nhóm
        // ALL là 400 VAL-17 ở service, và đóng băng một con số vào nhóm ALL cũng vô nghĩa vì service
        // luôn ép nhóm đó về 0.
        if (isDefaultGroup(next)) {
            next.priority = null
            next.prioritySource = 'computed'
            // Cùng lý do: nhóm mặc định mang tên cố định (VAL-19). Xoá NGAY tại đây thay vì để người
            // dùng phát hiện ô đã khoá mà chữ vẫn còn, rồi ăn 400 lúc bấm Lưu.
            next.name = null
        }

        onChange(next)
    }

    /** Người dùng sửa ô độ ưu tiên -> chuyển nhóm sang chế độ đặt tay. */
    const setPriority = (value) => {
        if (value === null || value === undefined || value === '') {
            update({priority: null, prioritySource: 'computed'})

            return
        }

        update({priority: value, prioritySource: 'manual'})
    }

    // Cùng vị từ với service (`isEffectiveAt` ở domain) — xem ghi chú ở helpers/priority.js.
    const effective = isEffectiveAt(group)
    const notYet = !effective && !!group.effectiveFrom && new Date(group.effectiveFrom).getTime() > Date.now()

    const renderHeader = () => (
        <div className="VcGroupHead" onClick={onToggle} role="presentation">
            <span className={`VcGroupNo ${isAllGroup ? 'VcGroupNo--all' : ''}`}>
                {isAllGroup ? 'A' : index + 1}
            </span>
            <span className="VcGroupSummary">
                {/*
                  * Ba dạng tên, theo thứ tự ưu tiên:
                  *   · nhóm mặc định  -> tên cố định, không đặt riêng được (VAL-19)
                  *   · có đặt tên     -> tên đó, kèm số thứ tự mờ phía sau
                  *   · chưa đặt tên   -> "Nhóm N" như trước
                  *
                  * ⚠️ Số thứ tự phải CÒN LẠI cả khi nhóm đã có tên: cảnh báo V-07 (cấu hình mơ hồ)
                  *    và V-08 gọi nhóm bằng SỐ ("Nhóm 2 ↔ Nhóm 5"), nên bỏ số đi là người đọc cảnh
                  *    báo không tìm được thẻ nào ứng với dòng nào.
                  */}
                <strong className="VcGroupName" title={group.name || undefined}>
                    {isAllGroup ? DEFAULT_GROUP_LABEL : (group.name || '').trim() || `Nhóm ${index + 1}`}
                </strong>
                {!isAllGroup && !!group.name && <span className="VcGroupOrdinal">#{index + 1}</span>}
                <Tag>{group.skuScope === 'all' ? 'Mọi SKU' : `${formatInt((group.skuList || []).length)} SKU`}</Tag>
                {!!materialCodes.length && (
                    <>
                        {materialCodes.map((code) => (
                            <span key={code}>
                                <Tag color="purple">{materialName(code)}</Tag>
                                <code className="VcMaterialCode">{code}</code>
                            </span>
                        ))}
                    </>
                )}
            </span>
            {/*
              * Nhãn "chưa/đã hết hiệu lực" nằm NGAY trên thẻ, không chỉ ở dòng tổng phía trên. Nhóm
              * này vẫn được lưu và vẫn sửa được, nhưng KHÔNG được tính vào cảnh báo độ phủ — thiếu
              * nhãn thì người dùng thấy một thẻ "Mọi SKU" nằm sờ sờ mà màn hình vẫn kêu "chưa có
              * ALL", và kết luận hợp lý nhất của họ là màn hình hỏng.
              */}
            {!effective && (
                <Tag color="orange">
                    {notYet ? 'Chưa tới hiệu lực' : 'Đã hết hiệu lực'}
                </Tag>
            )}
            {group.verifyStatus === 'verified' ? (
                <Tag color="green">{GROUP_VERIFY_LABELS.verified}</Tag>
            ) : (
                <Tag>{GROUP_VERIFY_LABELS.unverified}</Tag>
            )}
            {/*
              * Khối SỐ LIỆU của header — độ ưu tiên và chỉ số. Gom vào một thẻ bọc để nó không co lại
              * khi tên/tag phạm vi dài: đây đúng là phần người dùng quét mắt khi mọi thẻ đang thu gọn.
              */}
            <span className="VcGroupMeta">
                {/*
                  * Độ ưu tiên là thứ quyết định nhóm nào THẮNG khi nhiều nhóm cùng khớp — thu gọn mà
                  * không thấy nó thì không so sánh được hai thẻ với nhau.
                  *
                  * Phân biệt đặt-tay bằng CẢ màu lẫn hậu tố chữ, không chỉ màu: bảng này còn được in
                  * ra giấy đen trắng, và màu một mình thì người mù màu không đọc được.
                  */}
                <Tag
                    className="VcGroupPriority"
                    color={manualPriority && !isAllGroup ? 'blue' : undefined}
                    title={
                        manualPriority && !isAllGroup
                            ? 'Độ ưu tiên do người dùng đặt tay — không tự đổi khi sửa điều kiện nhóm'
                            : 'Độ ưu tiên hệ thống chấm theo điều kiện — tự đổi khi sửa điều kiện nhóm'
                    }
                >
                    ƯT {formatInt(effectivePriority(group))}{manualPriority && !isAllGroup ? ' · tay' : ''}
                </Tag>
                {zeroRate ? (
                    // Chữ nói NGHĨA NGHIỆP VỤ, không nhắc lại con số — số 0 đã nằm ngay trong ô chỉ
                    // số ở thân thẻ. Màu CAM chứ không đỏ: đây là cấu hình hợp lệ và cố ý, đỏ nghĩa
                    // là "sai, phải sửa".
                    <Tag color="orange" title="Chỉ số 0 — SKU/NVL khớp nhóm này không đi qua công đoạn, tính 0 giờ THẬT (vẫn là ĐÃ có định mức).">
                        0 giây — không qua công đoạn
                    </Tag>
                ) : (
                    <span className="VcGroupRate">{formatRate(group.rate)} giây/{unitText}</span>
                )}
            </span>
            {!readOnly && (
                <span className="VcGroupButtons" onClick={(event) => event.stopPropagation()} role="presentation">
                    <Button size="small" onClick={onDuplicate}>Nhân bản</Button>
                    <Button size="small" danger onClick={onRemove}>Xóa</Button>
                </span>
            )}
            {collapsed
                ? <DownOutlined className="VcGroupChevron"/>
                : <UpOutlined className="VcGroupChevron"/>}
        </div>
    )

    return (
        <div className={`VcGroup ${isAllGroup ? 'VcGroup--all' : ''}`}>
            {renderHeader()}

            {!collapsed && (
                <div className="VcGroupBody">
                    <div className="VcGroupCol">
                        <div className="VcColTitle">ĐIỀU KIỆN ÁP DỤNG</div>

                        <div className="VcField">
                            <label>SKU áp dụng <span className="VcMuted">— bắt buộc chọn</span></label>
                            <Radio.Group
                                className="VcScopeRadio"
                                disabled={readOnly}
                                value={group.skuScope}
                                onChange={(event) => update({
                                    skuScope: event.target.value,
                                    // Chọn "Mọi SKU" thì xóa toàn bộ SKU đã nhập (§4.2).
                                    skuList: event.target.value === 'all' ? [] : group.skuList,
                                })}
                            >
                                <Radio value="all">Mọi SKU (ALL)</Radio>
                                <Radio value="list">Danh sách cụ thể</Radio>
                            </Radio.Group>
                        </div>

                        {group.skuScope === 'list' && (
                            <div className="VcField">
                                {/*
                                  * Chọn thẳng từ danh mục, gõ để tìm. `tokenSeparators` cho phép dán
                                  * nhanh vài mã ngăn bằng dấu phẩy / xuống dòng ngay tại ô này; danh
                                  * sách dài vẫn nên dùng "Dán hàng loạt" vì bên đó có đối chiếu danh
                                  * mục và báo rõ mã nào không nhận (V-10 / V-11).
                                  */}
                                <Select
                                    mode="multiple"
                                    showSearch
                                    allowClear
                                    className="VcSkuSelect"
                                    disabled={readOnly}
                                    placeholder="Gõ mã SKU để tìm, hoặc dán nhiều mã ngăn bằng dấu phẩy"
                                    value={group.skuList || []}
                                    optionFilterProp="value"
                                    /*
                                      * Gõ là HỎI SERVER, không chỉ lọc tại chỗ: danh mục lớn hơn một
                                      * trang mà hook không xin `limit`, nên lọc trên tập đã nạp sẽ im
                                      * lặng giấu mọi SKU ngoài trang đầu — gõ đúng mã vẫn "không có".
                                      * Kết quả được GỘP vào tập đang có nên `optionFilterProp` vẫn
                                      * lọc như cũ.
                                      *
                                      * ⚠️ Ba trạng thái, ba câu khác nhau. Gộp "tìm hỏng" vào "không
                                      *    có mã" là tái lập đúng lỗi câm mà ô này sinh ra để diệt:
                                      *    ô là `mode="multiple"` nên mã gõ tay không khớp option sẽ
                                      *    bị NUỐT, người dùng không còn đường nào ngoài Dán hàng loạt.
                                      */
                                    onSearch={onSkuSearch}
                                    notFoundContent={skuSearchFailed
                                        ? 'Tìm hỏng — thử lại, hoặc dùng "Dán hàng loạt"'
                                        : (skuSearching ? 'Đang tìm…' : 'Không có SKU khớp')}
                                    tokenSeparators={[',', '\n', ';', '|']}
                                    maxTagCount={12}
                                    maxTagPlaceholder={(rest) => `+${rest.length} SKU nữa`}
                                    onChange={(value) => update({
                                        // Cắt khoảng trắng, viết hoa, bỏ trùng. Đường "Dán hàng loạt"
                                        // để service chuẩn hoá rồi trả về mã đã chuẩn; ô này gõ tay
                                        // nên phải tự làm, nếu không cùng một SKU vào hai đường lại
                                        // thành hai dòng khác nhau trong nhóm.
                                        skuList: [...new Set(
                                            value.map((item) => `${item}`.trim().toUpperCase()).filter(Boolean)
                                        )],
                                    })}
                                >
                                    {skus.map((sku) => (
                                        <Option key={sku.skuId} value={sku.skuId}>
                                            {sku.skuId}
                                            {sku.active === false && <span className="VcMuted"> — đang tắt</span>}
                                        </Option>
                                    ))}
                                </Select>
                                <div className="VcFieldFoot">
                                    {!readOnly && (
                                        <Button size="small" onClick={onPasteSku}>Dán hàng loạt</Button>
                                    )}
                                    <span className="VcMuted">{formatInt((group.skuList || []).length)} SKU</span>
                                </div>
                            </div>
                        )}

                        <div className="VcField">
                            <label>Nguyên vật liệu</label>
                            {byDetect ? (
                                <>
                                    <Select
                                        mode="multiple"
                                        showSearch
                                        allowClear
                                        disabled={readOnly}
                                        className={`VcMaterialSelect ${materialCodes.length ? 'VcMaterialSelect--set' : ''}`}
                                        placeholder="Gõ để tìm NVL… (để trống = mọi NVL)"
                                        value={materialCodes}
                                        optionFilterProp="children"
                                        // `|| []` vì antd trả `undefined` khi bấm nút xoá tất cả.
                                        onChange={(value) => update({materialCodes: value || []})}
                                    >
                                        {materials.map((item) => (
                                            <Option key={item.materialCode} value={item.materialCode}>
                                                {item.materialCode} — {item.name}
                                            </Option>
                                        ))}
                                    </Select>
                                    {!!materialCodes.length && !readOnly && (
                                        <div className="VcFieldFoot">
                                            <span className="VcMuted">{formatInt(materialCodes.length)} NVL</span>
                                            <Button size="small" onClick={() => update({materialCodes: []})}>
                                                Bỏ chọn
                                            </Button>
                                        </div>
                                    )}
                                    <div className="VcHint">
                                        Chọn được nhiều NVL — chúng dùng CHUNG một chỉ số. Muốn mỗi NVL một
                                        chỉ số khác thì tách thành nhiều nhóm. Để trống = áp cho mọi NVL.
                                        <div className="mt-1">
                                            Hai nhóm có NVL trùng nhau (dù chỉ một mã) và cùng độ ưu tiên sẽ
                                            bị coi là cấu hình mơ hồ.
                                        </div>
                                    </div>
                                </>
                            ) : (
                                // V-03: công đoạn tính một lần cho cả item/đơn thì ô bị khoá, không
                                // phát sinh thông báo lỗi.
                                <div className="VcNotApplicable">
                                    Không áp dụng — chỉ số của công đoạn này tính một lần cho cả item
                                    hoặc cả đơn, không tách theo NVL.
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="VcGroupCol">
                        <div className="VcColTitle">CHỈ SỐ TIÊU CHUẨN</div>

                        <div className="VcField VcRateField">
                            <InputNumber
                                disabled={readOnly}
                                className="VcRateInput"
                                min={0}
                                step={1}
                                value={group.rate}
                                onChange={(value) => update({rate: value})}
                                // V-05: tự làm tròn khi rời ô, không báo lỗi. Làm tròn LÊN thành số
                                // nguyên và ghi luôn vào form — bấm Lưu là xuống DB đúng con số này.
                                // Vẫn cho gõ số lẻ (không đặt `precision`) để người nhập điền đúng
                                // số đo gốc rồi hệ thống tự làm tròn, thay vì tự nhẩm tròn trong đầu.
                                // Chỉ ghi khi giá trị THỰC SỰ đổi: `update` gọi `onChange` vô
                                // điều kiện → `markDirty()`, nên click vào rồi click ra (không gõ
                                // gì) cũng bật "có thay đổi chưa lưu" và bung modal cảnh báo khi
                                // rời màn.
                                onBlur={() => {
                                    const rounded = roundRate(group.rate)

                                    if (rounded !== group.rate) update({rate: rounded})
                                }}
                            />
                            <span className="VcRateUnit">giây / {unitText}</span>
                            <div className="VcHint">Một con số duy nhất trên một đơn vị đo.</div>
                            {/*
                              * Lớp `VcWarning` không chỉ để đổi màu: nó cũng là thứ giữ dòng này HIỆN
                              * khi người dùng bật "Ẩn gợi ý" (rule `--noHints` chừa `.VcWarning` ra).
                              * Đây là cảnh báo về một cấu hình bất thường, không phải hướng dẫn dùng
                              * màn hình.
                              */}
                            {zeroRate && (
                                <div className="VcHint VcWarning">
                                    Chỉ số 0 nghĩa là{' '}
                                    <strong>
                                        {byDetect ? 'NVL trong nhóm này' : 'SKU trong nhóm này'} không đi qua công đoạn
                                    </strong>{' '}
                                    — hệ thống tính 0 giờ và coi như <strong>ĐÃ có định mức</strong>, nên chúng sẽ
                                    không còn nằm trong cảnh báo thiếu định mức. Nếu ý bạn là “chưa đo được” thì hãy{' '}
                                    <strong>xoá nhóm này</strong> thay vì để 0.
                                </div>
                            )}
                        </div>

                        <div className="VcField">
                            <label>Hiệu lực từ</label>
                            <DatePicker
                                disabled={readOnly}
                                format="DD/MM/YYYY"
                                allowClear={false}
                                value={group.effectiveFrom ? moment(group.effectiveFrom) : null}
                                onChange={(value) => update({effectiveFrom: dateOnlyIso(value)})}
                            />
                            <div className="VcHint">
                                Chọn được ngày trong quá khứ — dùng khi đo xong mới nhập cho kỳ trước;
                                số đã tính không bị áp ngược lại. Chọn ngày TƯƠNG LAI thì nhóm được lưu
                                nhưng <strong>chưa chạy</strong> tới ngày đó, và chưa được tính vào các
                                cảnh báo độ phủ.
                            </div>
                        </div>

                        <div className="VcField">
                            <label>
                                Đến ngày <span className="VcMuted">— để trống = còn hiệu lực</span>
                            </label>
                            <DatePicker
                                disabled={readOnly}
                                format="DD/MM/YYYY"
                                placeholder="Còn hiệu lực"
                                value={group.effectiveTo ? moment(group.effectiveTo) : null}
                                onChange={(value) => update({effectiveTo: dateOnlyIso(value)})}
                            />
                            <div className="VcHint">
                                Mốc kết thúc <strong>KHÔNG tính vào khoảng</strong> (đến hết ngày hôm trước).
                                Đặt mốc đã qua để ghi lại một khoảng lịch sử — nhóm đó chuyển sang hết hiệu
                                lực nên <strong>không hiện ở màn này nữa</strong>, xem tại nút Lịch sử.
                            </div>
                        </div>

                        {example && (
                            <div className="VcExample">
                                <div className="VcExampleLabel">{example.label}</div>
                                <div className="VcExampleCalc">
                                    {formatRate(group.rate)} × {formatInt(example.quantity)} ={' '}
                                    {formatInt(Math.ceil(ceilTo(group.rate || 0) * example.quantity))} giây
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="VcGroupCol">
                        <div className="VcColTitle">ĐỘ ƯU TIÊN &amp; THEO DÕI</div>

                        {/*
                          * Tên nhóm ở cột 3 chứ không cột 1: cột 1 là ĐIỀU KIỆN ÁP DỤNG (thứ quyết
                          * định nhóm khớp gì), cột 2 là con số. Tên là nhãn cho người đọc, cùng loại
                          * với `verifyStatus` và ghi chú — nó không tham gia phép tra cứu nào.
                          */}
                        <div className="VcField">
                            <label>
                                Tên nhóm <span className="VcMuted">— tuỳ chọn, không cần duy nhất</span>
                            </label>
                            <Input
                                disabled={readOnly || isAllGroup}
                                maxLength={GROUP_NAME_MAX_LENGTH}
                                placeholder={isAllGroup ? DEFAULT_GROUP_LABEL : 'VD: Gỗ 5 ly khổ lớn'}
                                value={group.name || ''}
                                /*
                                 * ⚠️ KHÔNG `trim()` ở `onChange`. Ô này là input điều khiển, nên
                                 *    cắt khoảng trắng ngay lúc gõ sẽ nuốt luôn dấu cách vừa bấm và
                                 *    người dùng không gõ nổi "Gỗ 5 ly". Cắt ở `onBlur` — lúc đó
                                 *    người ta đã gõ xong.
                                 */
                                onChange={(event) => update({name: event.target.value || null})}
                                onBlur={(event) => {
                                    // Chỉ ghi khi giá trị THỰC SỰ đổi — cùng lý do với ô Chỉ số ở
                                    // trên: `update` gọi `onChange` vô điều kiện → `markDirty()`,
                                    // nên click vào rồi click ra (không gõ gì) cũng bật "có thay
                                    // đổi chưa lưu" và bung modal cảnh báo oan khi rời màn.
                                    const trimmed = event.target.value.trim() || null

                                    if (trimmed !== (group.name || null)) update({name: trimmed})
                                }}
                            />
                            <div className="VcHint">
                                {isAllGroup
                                    ? 'Nhóm áp cho mọi SKU và mọi NVL mang tên cố định — không đổi được.'
                                    : 'Chỉ là nhãn để gọi tên nhóm trên màn hình, trong cảnh báo và trong file Excel. ' +
                                      'KHÔNG ảnh hưởng phép tra cứu.'}
                            </div>
                        </div>

                        <div className="VcField VcPriorityField">
                            <label>
                                Độ ưu tiên{' '}
                                <span className="VcMuted">— điểm cao thắng khi nhiều nhóm cùng khớp</span>
                            </label>
                            <InputNumber
                                className="VcPriorityInput"
                                // Nhóm ALL khoá cứng ở 0: nó phải luôn thua mọi nhóm có điều kiện.
                                disabled={readOnly || isAllGroup}
                                min={0}
                                max={PRIORITY_MANUAL_MAX}
                                step={10}
                                // DTO phía service bắt `@IsInt()`, nên số lẻ bị ValidationPipe chặn
                                // TRƯỚC khi tới service — `data` lúc đó là mảng CHUỖI tiếng Anh chứ
                                // không phải mảng object có mã VAL, nên màn hình chỉ hiện được
                                // `groups.0.priority must be an integer number`. Chặn ngay ở ô nhập
                                // là cách duy nhất nói được bằng tiếng Việt.
                                precision={0}
                                value={effectivePriority(group)}
                                onChange={setPriority}
                            />
                            {/*
                             * CHỈ nói khi đang đặt tay. Nhãn "chấm tự động" là trạng thái mặc định nên không mang
                             * thông tin, mà vẫn chiếm một dòng — và nó trùng nghĩa với nút "Về mặc định" (nút chỉ
                             * hiện khi đã đặt tay, nên sự có mặt của nút đã là nhãn).
                             *
                             * Điều đáng nói không phải con số mà là nó CÓ TỰ ĐỔI hay không: chấm tự động thì sửa
                             * điều kiện là điểm tự nhảy (thêm NVL: 0 → 10), còn đặt tay thì giữ nguyên. Người dùng
                             * gõ đúng con số hệ thống đang chấm (50) sẽ không nhận ra mình vừa đóng băng nó.
                             */}
                            {manualPriority && !isAllGroup && (
                                <div className="VcFieldFoot">
                                    <span className="VcMuted">Đặt tay — không tự đổi khi sửa điều kiện nhóm</span>
                                    {!readOnly && (
                                        <Button
                                            size="small"
                                            onClick={() => update({priority: null, prioritySource: 'computed'})}
                                        >
                                            Về mặc định
                                        </Button>
                                    )}
                                </div>
                            )}
                            <div className="VcHint">
                                {isAllGroup
                                    ? 'Nhóm mặc định luôn 0 điểm và luôn thua mọi nhóm khác — không sửa được.'
                                    : `Mặc định chấm theo điều kiện: danh sách SKU +${PRIORITY_POINTS.SKU_LIST}, `
                                        + `có NVL +${PRIORITY_POINTS.MATERIAL}. Hai nhóm cùng điểm mà phạm vi `
                                        + 'giao nhau thì bị coi là cấu hình mơ hồ — đặt hai số KHÁC nhau là cách '
                                        + 'khai rõ nhóm nào thắng.'}
                            </div>
                        </div>

                        <div className="VcField">
                            <label>
                                Trạng thái{' '}
                                <span className="VcMuted">— nhãn theo dõi, KHÔNG ảnh hưởng phép tính</span>
                            </label>
                            <Radio.Group
                                disabled={readOnly}
                                value={group.verifyStatus || 'unverified'}
                                onChange={(event) => update({verifyStatus: event.target.value})}
                            >
                                <Radio.Button value="unverified">{GROUP_VERIFY_LABELS.unverified}</Radio.Button>
                                <Radio.Button value="verified">{GROUP_VERIFY_LABELS.verified}</Radio.Button>
                            </Radio.Group>
                            <div className="VcHint">
                                Nhóm <strong>Chưa verify</strong> VẪN được dùng để tính giờ — đây chỉ là dấu
                                để biết số nào đã đối chiếu với thực tế xưởng, số nào còn là ước lượng.
                            </div>
                        </div>

                        <div className="VcField">
                            <label>
                                Ghi chú <span className="VcMuted">— nguồn số, ai đo, đo khi nào</span>
                            </label>
                            {/* `showCount` chỉ có từ antd 4.7, repo đang ghim 4.5.4 — tự đếm. */}
                            <Input.TextArea
                                disabled={readOnly}
                                rows={2}
                                maxLength={GROUP_NOTE_MAX_LENGTH}
                                placeholder="VD: đo từ log prod 30 ngày, P90 — IE Nam, 09/09/2026"
                                value={group.note || ''}
                                onChange={(event) => update({note: event.target.value || null})}
                            />
                            <div className="VcFieldFoot">
                                <span className="VcMuted">
                                    {formatInt((group.note || '').length)}/{formatInt(GROUP_NOTE_MAX_LENGTH)} ký tự
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

GroupCard.propTypes = {
    group: PropTypes.object.isRequired,
    index: PropTypes.number.isRequired,
    materials: PropTypes.array.isRequired,
    skus: PropTypes.array,
    skuSearching: PropTypes.bool,
    skuSearchFailed: PropTypes.bool,
    onSkuSearch: PropTypes.func,
    rateUnit: PropTypes.string.isRequired,
    byDetect: PropTypes.bool.isRequired,
    collapsed: PropTypes.bool,
    readOnly: PropTypes.bool,
    onToggle: PropTypes.func.isRequired,
    onChange: PropTypes.func.isRequired,
    onDuplicate: PropTypes.func.isRequired,
    onRemove: PropTypes.func.isRequired,
    onPasteSku: PropTypes.func.isRequired,
}

GroupCard.defaultProps = {
    skus: [],
    skuSearching: false,
    skuSearchFailed: false,
    onSkuSearch: undefined,
}

export default GroupCard
